/**
 * ZIP Code to City/State Lookup
 * Uses free Zippopotam.us API for ZIP code lookup
 */

// Cache to avoid repeated API calls
const zipCache = {};

/**
 * Lookup city and state from ZIP code
 * @param {string} zipCode - 5-digit US ZIP code
 * @returns {Promise<{city: string, state: string, stateCode: string} | null>}
 */
export async function lookupZipCode(zipCode) {
  // Validate ZIP code format
  if (!zipCode || !/^\d{5}$/.test(zipCode)) {
    return null;
  }

  // Check cache first
  if (zipCache[zipCode]) {
    return zipCache[zipCode];
  }

  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
    
    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    
    if (data && data.places && data.places.length > 0) {
      const place = data.places[0];
      const result = {
        city: place['place name'].toUpperCase(),
        state: place['state'].toUpperCase(),
        stateCode: place['state abbreviation'].toUpperCase()
      };
      
      // Cache the result
      zipCache[zipCode] = result;
      
      return result;
    }
    
    return null;
  } catch (error) {
    console.error('ZIP code lookup failed:', error);
    return null;
  }
}

/**
 * State code to full name mapping
 */
export const STATE_NAMES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia'
};

export default lookupZipCode;
